// import { Card } from "@/Card";
import { LayoutHeader } from "@/Header";
import { useAppColor } from "^/common/useAppColor";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import useNotice from "^/customer/notice";
import { useUserNoticeReadAll } from "~/services/customer/notice";
import { useUserReserveNoticeRead } from "~/services/customer/reservation";
import { ReactNode } from "react";
import { IconCalendar } from "@/Icon/Calendar";
import { IconNotify } from "@/Icon/Notify";
import { IconGallery } from "@/Icon/Gallery";
import { IconNotice } from "@/Icon/Notice";

export interface NoticeType {
  notiRecvSeq?: number;
  type:
    | "NOTI_SCHEDULE"
    | "NOTI_RESERV_CONFIRM"
    | "NOTI_ANNOUNCEMENT"
    | "NOTI_DIARY"
    | "NOTI_RESERV_CANCEL"
    | "NOTI_ATTEND_STATUS_DISMISSED"
    | "NOTI_ATTEND_STATUS_ATTENDED";
  message: string;
  readYn: "Y" | "N";
  reservationSeq?: number;
  cdReservStatus?: string;
  cdReservType?: string;
  companyName?: string;
}
interface NoticeListType {
  date: string;
  notices: NoticeType[];
}

export const Route = createFileRoute("/customer/layout/home-alert")({
  component: RouteComponent,
});

function RouteComponent() {
  const { alertList, refetchAlertList } = useNotice();
  const { history } = useRouter();
  const readAllMutation = useUserNoticeReadAll();
  const reserveNoticeReadMutation = useUserReserveNoticeRead();

  useAppColor({
    background: "#f5f5f5",
  });

  const onClickAllRead = () => {
    console.log("onClickAllRead");

    readAllMutation.mutate(undefined, {
      onSuccess: (response) => {
        console.log("전체 읽음 처리 성공:", response);
        // 성공 시 alertList 재호출
        refetchAlertList();
      },
      onError: (error) => {
        console.error("전체 읽음 처리 실패:", error);
        alert("전체 읽음 처리에 실패했습니다. 다시 시도해주세요.");
      },
    });
  };

  const onClickNotice = (data: NoticeType) => {
    console.log(data);
    // 출석 상태 알림은 클릭해도 화면 이동 없음
    if (
      data.type === "NOTI_ATTEND_STATUS_DISMISSED" ||
      data.type === "NOTI_ATTEND_STATUS_ATTENDED"
    ) {
      return;
    }
    // 예약 확정/취소 알림 — 읽음 처리 후 예약 상세로 이동
    // (취소 알림도 상세로 이동: 취소됐더라도 예약했던 날짜·시간을 확인할 수 있어야 함)
    if (
      data.type === "NOTI_RESERV_CONFIRM" ||
      data.type === "NOTI_RESERV_CANCEL"
    ) {
      const goReservationDetail = () =>
        history.push(
          `/customer/layout/calendar-reservation-chg?cdReservStatus=${data.cdReservStatus}&cdReservType=${data.cdReservType}&reservationSeq=${data.reservationSeq}&companyName=${data.companyName}`
        );
      // notiRecvSeq가 있는 경우에만 읽음 처리 API 호출
      if (data.notiRecvSeq) {
        reserveNoticeReadMutation.mutate(
          { notiRecvSeq: data.notiRecvSeq },
          {
            onSuccess: goReservationDetail,
            onError: (error) => {
              console.error("알림 읽음 처리 실패:", error);
              // API 실패해도 페이지 이동
              goReservationDetail();
            },
          }
        );
      } else {
        goReservationDetail();
      }
    } else {
      // 다른 타입의 알림은 기존 로직대로 처리
      history.push(`/customer/layout/inform-detail?seq=${data.notiRecvSeq}`);
    }
  };

  return (
    <div className="flex-auto">
      <LayoutHeader
        title="새로운 알림을 확인하세요"
        hasBackBtn
        hasFootPrint={false}
      />
      <div className="flex justify-between px-5 py-1">
        <div className="flex">알림은 30일간 저장돼요</div>
        <div className="flex" onClick={onClickAllRead}>
          모두 읽음
        </div>
      </div>
      <div className="p-5">
        {alertList?.map((item: NoticeListType, index: number) => (
          <div key={index.toString()}>
            <div className="mb-2 text-sm font-semibold text-gray-500">
              {item.date}
            </div>
            <div className="bg-white rounded-lg p-4 mb-2 items-center border border-[#e9e9e9] grid gap-2">
              {item.notices.map((data: NoticeType) => (
                <Card
                  title={data.message}
                  type={data.type}
                  key={data.notiRecvSeq || `notice-${index}`}
                  readYn={data.readYn}
                  onClick={() => onClickNotice(data)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

interface CardProps extends IconProps {
  title?: string;
  children?: ReactNode;
  onClick?: () => void;
}
interface IconProps {
  type:
    | "NOTI_SCHEDULE"
    | "NOTI_RESERV_CONFIRM"
    | "NOTI_ANNOUNCEMENT"
    | "NOTI_DIARY"
    | "NOTI_RESERV_CANCEL"
    | "NOTI_ATTEND_STATUS_DISMISSED"
    | "NOTI_ATTEND_STATUS_ATTENDED";
  readYn: "Y" | "N";
}
const Icon = ({ type, readYn }: IconProps) => {
  console.log(type, readYn);
  switch (type) {
    case "NOTI_SCHEDULE":
      return <IconCalendar active={readYn === "N"} />;
    case "NOTI_RESERV_CONFIRM":
      return <IconNotify active={readYn === "N"} />;
    case "NOTI_RESERV_CANCEL":
      return <IconNotify active={readYn === "N"} />;
    case "NOTI_ATTEND_STATUS_DISMISSED":
      return <IconCalendar active={readYn === "N"} />;
    case "NOTI_ATTEND_STATUS_ATTENDED":
      return <IconCalendar active={readYn === "N"} />;
    case "NOTI_ANNOUNCEMENT":
      return <IconGallery active={readYn === "N"} />;
    case "NOTI_DIARY":
      return <IconNotice active={readYn === "N"} />;
  }
};
export const Card = ({ children, title, onClick, type, readYn }: CardProps) => {
  console.log({ type, readYn });
  return (
    <div className="w-full" onClick={onClick}>
      <p className="flex gap-2 items-center">
        <Icon type={type} readYn={readYn} />
        {title}
      </p>
      {children ?? null}
    </div>
  );
};
